import { useState, useEffect } from "react";
import WizardShell from "@/components/WizardShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import usePostcodeLookup from "@/lib/usePostcodeLookup";
import PhotoUpload from "@/components/PhotoUpload";
import Repeater from "@/components/Repeater";
import { MapPin } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const empty = { client_id: "", street: "", number: "", postal_code: "", city: "", type: "Woning", units: 1, contacts: [], notes: "", photo_url: "" };

export default function PropertyWizard({ open, onOpenChange, onSaved, initial, presetClient }) {
  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(initial || { ...empty, client_id: presetClient || "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const lookup = usePostcodeLookup((a) => setForm((f) => ({ ...f, street: a.street, city: a.city })));

  useEffect(() => {
    if (open) {
      api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
      setForm(initial?.id ? { ...empty, ...initial } : { ...empty, client_id: presetClient || "" });
      setStep(0);
    }
  }, [open]);

  const finish = async () => {
    setLoading(true);
    try {
      const payload = { ...form, units: Number(form.units) || 1 };
      if (initial?.id) await api.put(`/properties/${initial.id}`, payload);
      else await api.post("/properties", payload);
      toast.success("Pand opgeslagen");
      onSaved?.();
      onOpenChange(false);
      setStep(0); setForm({ ...empty, client_id: presetClient || "" });
    } catch (e) { toast.error("Opslaan mislukt"); }
    finally { setLoading(false); }
  };

  return (
    <WizardShell open={open} onOpenChange={onOpenChange}
      title={initial?.id ? "Pand bewerken" : "Nieuw Pand / Adres"}
      subtitle="Registreer een adres onder een opdrachtgever"
      steps={["Opdrachtgever", "Adres", "Details"]}
      step={step} setStep={setStep} onFinish={finish} loading={loading}
      canNext={(step === 0 && !!form.client_id) || (step === 1 && form.street.trim()) || step === 2}
      finishLabel="Pand opslaan">
      {step === 0 && (
        <div className="space-y-3">
          <Label>Kies opdrachtgever *</Label>
          <Combobox testId="property-client-select" value={form.client_id} onChange={(v) => set("client_id", v)} placeholder="Selecteer opdrachtgever" searchPlaceholder="Zoek opdrachtgever…" options={clients.map((c) => ({ value: c.id, label: c.name }))} />
          {clients.length === 0 && <p className="text-sm text-amber-600">Maak eerst een opdrachtgever aan.</p>}
        </div>
      )}
      {step === 1 && (
        <div className="space-y-4">
          <AddressAutocomplete onSelect={(a) => setForm((f) => ({ ...f, ...a }))} />
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2"><Label>Straat *</Label><Input data-testid="property-street-input" value={form.street} onChange={(e) => set("street", e.target.value)} /></div>
            <div><Label>Nr.</Label><Input data-testid="property-number-input" value={form.number} onChange={(e) => { set("number", e.target.value); lookup(form.postal_code, e.target.value); }} /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>Postcode</Label><Input data-testid="property-postal-input" value={form.postal_code} onChange={(e) => { set("postal_code", e.target.value); lookup(e.target.value, form.number); }} /></div>
            <div className="col-span-2"><Label>Plaats</Label><Input data-testid="property-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type</Label>
              <Combobox testId="property-type-select" value={form.type} onChange={(v) => set("type", v)} options={["Woning", "Appartement", "Bedrijfsruimte", "Kamer", "Garage"].map((t) => ({ value: t, label: t }))} />
            </div>
            <div><Label>Aantal eenheden</Label><Input type="number" data-testid="property-units-input" value={form.units} onChange={(e) => set("units", e.target.value)} /></div>
          </div>
          <div><Label>Notities</Label><Textarea rows={4} data-testid="property-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-2 block">Contactpersonen bij dit pand</Label>
            <Repeater items={form.contacts} onChange={(v) => set("contacts", v)} testid="property-contacts"
              fields={[{ key: "name", label: "Naam", full: true }, { key: "function", label: "Functie" }, { key: "phone", label: "Telefoon", normalize: true }, { key: "email", label: "E-mail", full: true }]}
              addLabel="Contactpersoon toevoegen" emptyHint="Nog geen contactpersonen bij dit pand." />
          </div>
          <PhotoUpload value={form.photo_url} onChange={(v) => set("photo_url", v)} label="Foto van het object" allowUrl testid="property-photo" />
          <Button type="button" variant="outline" size="sm" className="w-full" data-testid="property-streetview-button"
            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${form.street} ${form.number} ${form.postal_code} ${form.city}`)}`, "_blank")}>
            <MapPin className="h-4 w-4 mr-2" /> Bekijk op Google Maps / Street View
          </Button>
        </div>
      )}
    </WizardShell>
  );
}
